/**
 * Thin wrapper over the Google Maps JS "Places API (New)":
 * address suggestions while typing, then the picked place split into parts.
 *
 * Needs VITE_GOOGLE_MAPS_API_KEY with "Maps JavaScript API" and
 * "Places API (New)" enabled. Without it, autocomplete is simply off.
 */

const API_KEY = import.meta.env.VITE_GOOGLE_MAPS_API_KEY as string | undefined;

export const placesEnabled = Boolean(API_KEY);

/** A structured postal address. */
export interface AddressParts {
  /** Street number + route, e.g. "12 Rue de Rivoli". */
  street: string;
  city: string;
  /** Region / state / province (administrative_area_level_1). */
  state: string;
  postalCode: string;
  country: string;
}

export interface AddressSuggestion {
  id: string;
  /** Full one-line label, e.g. "12 Rue de Rivoli, Paris, France". */
  label: string;
  /** Resolves the suggestion to its address parts. */
  resolve: () => Promise<AddressParts>;
}

// Minimal typings for the bits of the Places library we use.
interface AddressComponent {
  longText: string | null;
  shortText: string | null;
  types: string[];
}
interface Place {
  addressComponents?: AddressComponent[];
  fetchFields(req: { fields: string[] }): Promise<unknown>;
}
interface PlacePrediction {
  placeId: string;
  text: { toString(): string };
  toPlace(): Place;
}
interface PlacesLibrary {
  AutocompleteSessionToken: new () => object;
  AutocompleteSuggestion: {
    fetchAutocompleteSuggestions(req: {
      input: string;
      sessionToken: object;
      includedPrimaryTypes?: string[];
    }): Promise<{ suggestions: { placePrediction: PlacePrediction | null }[] }>;
  };
}
declare global {
  interface Window {
    google?: { maps?: { importLibrary(name: string): Promise<unknown> } };
    __vtcMapsReady?: () => void;
  }
}

let libraryPromise: Promise<PlacesLibrary> | null = null;

/** Loads the Maps script once and returns the Places library. */
function loadPlaces(): Promise<PlacesLibrary> {
  if (!API_KEY) return Promise.reject(new Error("Google Maps API key missing"));
  libraryPromise ??= new Promise<void>((resolve, reject) => {
    if (window.google?.maps?.importLibrary) return resolve();
    window.__vtcMapsReady = () => resolve();
    const script = document.createElement("script");
    script.src =
      "https://maps.googleapis.com/maps/api/js" +
      `?key=${encodeURIComponent(API_KEY)}&loading=async&v=weekly&callback=__vtcMapsReady`;
    script.async = true;
    script.onerror = () => {
      libraryPromise = null;
      reject(new Error("Could not load Google Maps"));
    };
    document.head.appendChild(script);
  }).then(
    () => window.google!.maps!.importLibrary("places") as Promise<PlacesLibrary>,
  );
  return libraryPromise;
}

/** Splits Google's address components into our fields. */
export function toAddressParts(components: AddressComponent[]): AddressParts {
  const get = (type: string, short = false) => {
    const c = components.find((c) => c.types.includes(type));
    return (short ? c?.shortText : c?.longText) ?? "";
  };
  return {
    street: [get("street_number"), get("route")].filter(Boolean).join(" "),
    city:
      get("locality") ||
      get("postal_town") ||
      get("sublocality") ||
      get("administrative_area_level_2"),
    state: get("administrative_area_level_1"),
    postalCode: get("postal_code"),
    country: get("country"),
  };
}

/**
 * Creates an autocomplete session. Google bills one session for all the
 * keystrokes plus the final place lookup, so use one session per address.
 */
export function createAddressSession() {
  let token: object | null = null;

  return {
    async search(input: string): Promise<AddressSuggestion[]> {
      const places = await loadPlaces();
      token ??= new places.AutocompleteSessionToken();
      const { suggestions } =
        await places.AutocompleteSuggestion.fetchAutocompleteSuggestions({
          input,
          sessionToken: token,
        });

      return suggestions.flatMap(({ placePrediction: p }) =>
        p
          ? [
              {
                id: p.placeId,
                label: p.text.toString(),
                resolve: async () => {
                  const place = p.toPlace();
                  await place.fetchFields({ fields: ["addressComponents"] });
                  token = null; // the lookup closes the billing session
                  return toAddressParts(place.addressComponents ?? []);
                },
              },
            ]
          : [],
      );
    },
  };
}
